import type {
  ContractStatus,
  ContractSummaryDto,
  ContractTerminationReason,
  DeploymentReason,
  ListContractsResponse,
  PricingScheme,
} from '@bhops/shared';
import { apiClient } from './client';

export interface ListContractsQuery {
  status?: ContractStatus;
  customerId?: string;
  dealId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface TerminateContractBody {
  reason: ContractTerminationReason;
  note?: string;
}

// Wire-format CreateContractDto. BigInt fields ride as numeric
// strings per `decisions.md §C-6`; date fields are ISO 8601.
// Mirrors `apps/api/src/contracts/dto/create-contract.dto.ts`.
export interface CreateContractLineBody {
  modelId: string;
  quantity: number;
  reason: DeploymentReason;
  unitPriceMonthly: string;
  currency: string; // ISO-4217
  effectiveFrom: string;
  notes?: string;
}

export interface CreateContractPricingBody {
  scheme: PricingScheme;
  totalMonthly?: string | null;
  totalOneTime?: string | null;
  deposit?: string | null;
  currency: string;
  exchangeRateSnapshot: string; // numeric string ("1.0" for KRW)
  effectiveFrom: string;
  paymentSchedule?: string;
}

// PATCH /contracts/:id — DRAFT-only header edits. ACTIVE+
// contracts are immutable (BR-CNT-06). Lines / pricings are not
// editable here.
export interface UpdateContractBody {
  startDate?: string;
  endDate?: string;
  durationMonths?: string;
  renewalScheduledAt?: string | null;
}

export interface CreateContractBody {
  dealId: string;
  startDate: string;
  endDate: string;
  durationMonths: string; // numeric string, Decimal(5,2)
  renewalScheduledAt?: string;
  lines: CreateContractLineBody[];
  pricings: CreateContractPricingBody[];
}

export const contractsApi = {
  async list(query: ListContractsQuery = {}): Promise<ListContractsResponse> {
    const { data } = await apiClient.get<ListContractsResponse>('/contracts', { params: query });
    return data;
  },
  async downloadPdf(id: string): Promise<void> {
    const res = await apiClient.get<Blob>(`/contracts/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers['content-disposition'] as string | undefined;
    a.download = disposition?.match(/filename="([^"]+)"/)?.[1] ?? `contract-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
  async get(id: string): Promise<ContractSummaryDto> {
    const { data } = await apiClient.get<ContractSummaryDto>(`/contracts/${id}`);
    return data;
  },
  async create(body: CreateContractBody): Promise<ContractSummaryDto> {
    const { data } = await apiClient.post<ContractSummaryDto>('/contracts', body);
    return data;
  },
  async update(id: string, body: UpdateContractBody): Promise<ContractSummaryDto> {
    const { data } = await apiClient.patch<ContractSummaryDto>(`/contracts/${id}`, body);
    return data;
  },
  async sign(id: string): Promise<ContractSummaryDto> {
    const { data } = await apiClient.post<ContractSummaryDto>(`/contracts/${id}/sign`);
    return data;
  },
  async terminate(id: string, body: TerminateContractBody): Promise<ContractSummaryDto> {
    const { data } = await apiClient.post<ContractSummaryDto>(`/contracts/${id}/terminate`, body);
    return data;
  },
  // round7 §1-1 — 계약 퍼지(전 상태, ADMIN). 서버가 계약번호 일치 재검증.
  async purge(id: string, confirmHumanId: string): Promise<{ purged: Record<string, number> }> {
    const { data } = await apiClient.post<{ purged: Record<string, number> }>(
      `/admin/contracts/${id}/purge`,
      { confirmHumanId },
    );
    return data;
  },
  async deleteDraft(id: string): Promise<{ id: string }> {
    const { data } = await apiClient.delete<{ id: string }>(`/contracts/${id}`);
    return data;
  },
  // batch-2 §4 — InstallCalendar 의 drag-to-reschedule
  async rescheduleInstall(
    id: string,
    scheduledInstallAt: string | null,
  ): Promise<{
    id: string;
    humanId: string;
    scheduledInstallAt: string | null;
    handoffStatus: string;
  }> {
    const { data } = await apiClient.post(`/contracts/${id}/reschedule-install`, {
      scheduledInstallAt,
    });
    return data;
  },
};
